import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, Facebook, Instagram, Music2, Hash } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function ShareModal({ open, onClose, url }) {
  const { user } = useAuth();
  const isPro = user?.subscription?.is_master || user?.subscription?.tier === "pro" || user?.subscription?.tier === "pro_trial";
  const shareUrl = url || (typeof window !== "undefined" ? window.location.origin : "");

  const channels = [
    { id: "facebook", label: "Facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` },
    { id: "instagram", label: "Instagram", icon: Instagram, href: "https://www.instagram.com" },
    { id: "threads", label: "Threads", icon: Hash, href: `https://www.threads.net/intent/post?text=${encodeURIComponent(shareUrl)}` },
    { id: "tiktok", label: "TikTok", icon: Music2, href: "https://www.tiktok.com/upload" },
    { id: "pinterest", label: "Pinterest", icon: Hash, href: `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}` },
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="share-modal">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Share design</DialogTitle>
        </DialogHeader>

        {!isPro && (
          <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded p-2 mb-2">
            Social channel sharing is a Pro feature. Link copy is available on all plans.
          </div>
        )}

        <div className="flex items-center gap-2">
          <Input value={shareUrl} readOnly data-testid="share-url-input" className="text-xs font-mono" />
          <Button
            variant="outline"
            onClick={() => {
              navigator.clipboard.writeText(shareUrl);
              toast.success("Link copied");
            }}
            data-testid="share-copy-button"
          >
            <Copy className="w-4 h-4" />
          </Button>
        </div>

        <div className="grid grid-cols-5 gap-2 mt-4">
          {channels.map((c) => {
            const Icon = c.icon;
            return (
              <a
                key={c.id}
                href={isPro ? c.href : undefined}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  if (!isPro) {
                    e.preventDefault();
                    toast.error("Upgrade to Pro to share to social channels");
                  }
                }}
                className={`aspect-square rounded-md border border-gray-200 flex flex-col items-center justify-center text-xs gap-1 hover:bg-gray-50 ${
                  isPro ? "" : "opacity-50 cursor-not-allowed"
                }`}
                data-testid={`share-channel-${c.id}`}
              >
                <Icon className="w-5 h-5 text-[#002FA7]" />
                {c.label}
              </a>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
